import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import InfoIcon from "@mui/icons-material/Info";
import { Box, FormControlLabel, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { msspDashboardKeys } from "../../../../api/queryKeys";
import CustomSwitch from "../../../../components/CustomSwitch";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import PieChartTool from "./PieChartTool";
import SeverityLegend from "./SeverityLegend";
import ToolSeveritiesDrawer from "./ToolSeveritiesDrawer";
import { sortToolsByHighSeverity } from "./utils";

const StackUtilizationAndCoverage = () => {
  const colors = useColors();
  const scrollContainerRef = useRef(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [showNotDeployed, setShowNotDeployed] = useState(false);
  const { getMSSPStackUtilizationCoverage, trackEvent } = useAPI();

  const {
    data: allTenantsControlsByTools,
    isLoading: isLoadingAllTenantsControlsByTools,
  } = useQuery({
    queryKey: msspDashboardKeys.stackUtilizationCoverage(),
    queryFn: getMSSPStackUtilizationCoverage,
    select: (data) => data.data,
  });

  const sortedAllTenantsControlsByTools = useMemo(() => {
    if (!allTenantsControlsByTools) return [];
    return sortToolsByHighSeverity(allTenantsControlsByTools, showNotDeployed);
  }, [allTenantsControlsByTools, showNotDeployed]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
      trackEvent("User scrolled to left in Stack Utilization and Coverage");
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      trackEvent("User scrolled to right in Stack Utilization and Coverage");
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
      }
    };

    const handleResize = () => {
      handleScroll();
    };

    const scrollContainer = scrollContainerRef.current;
    const resizeObserver = new ResizeObserver(handleResize);

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      resizeObserver.observe(scrollContainer);
    }

    // Initial check
    handleScroll();

    // Check scroll when data loads
    if (allTenantsControlsByTools && !isLoadingAllTenantsControlsByTools) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(handleScroll, 100);
    }

    // Cleanup event listener and observer on component unmount
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
        resizeObserver.unobserve(scrollContainer);
      }
    };
  }, [allTenantsControlsByTools, isLoadingAllTenantsControlsByTools]);

  return (
    <Box
      sx={{
        backgroundColor: colors.common_white,
        borderRadius: "10px",
        boxShadow: "0px 0px 2px 1px " + colors.mssp_box_shadow_color,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "40px",
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1">
            Stack Utilization and Coverage
          </Typography>
          <CustomTooltip title="Displays how many controls are deployed and compliant across all environments or subsidiaries.">
            <InfoIcon
              sx={{
                color: colors.tooltip_info_icon,
                width: "16px",
                height: "16px",
                cursor: "pointer",
              }}
            />
          </CustomTooltip>
        </Box>
        <FormControlLabel
          sx={{
            background: colors.common_white,
            color: colors.contrast_text_dark,
            height: "32px",
            textTransform: "unset",
            borderRadius: "8px",
            margin: "0",
            gap: 1,
            padding: "8px",
          }}
          control={
            <CustomSwitch
              checked={showNotDeployed}
              onChange={() => {
                setShowNotDeployed(!showNotDeployed);
              }}
            />
          }
          label={<Typography variant="caption">Show not deployed</Typography>}
          labelPlacement="start"
        />
      </Box>
      {isLoadingAllTenantsControlsByTools ? (
        <Box>
          <LoadingIcon />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <IconButton
            onClick={scrollLeft}
            disabled={isAtStart}
            sx={{
              width: "24px",
              height: "24px",
              position: "absolute",
              left: 0,
              zIndex: 2,
              backgroundColor: colors.common_white,
              "&:hover": {
                backgroundColor: colors.common_white,
              },
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ArrowBackIosRoundedIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: 1,
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "& > *": {
                flex: "0 0 auto",
                width: "200px",
              },
              margin: "0 40px", // Add margin to prevent content from being hidden behind buttons
              width: "calc(100% - 80px)", // Ensure the scroll container takes full width minus margins
            }}
          >
            {sortedAllTenantsControlsByTools
              .filter(
                (data) => !(!showNotDeployed && data.tenants?.length === 0) // If not showing not deployed, filter out tools with no tenants
              )
              .map((data) => (
                <PieChartTool
                  key={data.id}
                  data={data}
                  setSelectedTool={setSelectedTool}
                  showNotDeployed={showNotDeployed}
                />
              ))}
          </Box>
          <IconButton
            onClick={scrollRight}
            disabled={isAtEnd}
            sx={{
              width: "24px",
              height: "24px",
              position: "absolute",
              right: 0,
              zIndex: 2,
              backgroundColor: colors.common_white,
              "&:hover": {
                backgroundColor: colors.common_white,
              },
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ArrowForwardIosRoundedIcon
              sx={{ width: "16px", height: "16px" }}
            />
          </IconButton>
          <ToolSeveritiesDrawer
            selectedTool={selectedTool}
            open={!!selectedTool}
            onClose={() => setSelectedTool(null)}
          />
        </Box>
      )}
      <SeverityLegend />
    </Box>
  );
};

export default StackUtilizationAndCoverage;
